import * as PIXI from 'pixi.js-legacy';
import Mask from '../Mask/Mask';
import Grid from './Grid';
import 'regenerator-runtime/runtime';

export const GRID_CALLBACK_TYPES = {
  MASK_UPDATE: 'mask-update',
  SEQUENCE_BEGIN: 'sequence-begin',
  SEQUENCE_COMPLETED: 'sequence-completed',
  FRAME_COMPLETE: 'frame-complete',
};

/**
 * Convenience class around the Grid Framework to help setup up grids.
 */
class GridPlayer {
  /**
   * Contains the mask update callbacks.
   */
  callbacks = [];

  /**
   * Initializes the canvas based on the given HTMLElement.
   * 	eg takes up the size in px; height/width.
   *
   * @param {HTMLElement} element
   */
  constructor(element, { background = null, color = 0 }) {
    const { width, height } = element.getBoundingClientRect();

    this.app = new PIXI.Application({
      resolution: 1,
      resizeTo: element,
      transparent: !!background,
      backgroundColor: background ? background : undefined,
      antialias: true,
    });

    element.appendChild(this.app.view);

    this.grid = new Grid(this.app, width, height, 1, color);
  }

  /**
   * Sets and loads the masks on the grid.
   *
   * @param {Array<>} masks
   */
  async setMasks(masks) {
    this.masks = new Mask(this.app, [this.grid], masks);
    // try {
    await this.masks.loadAssets();
    // } catch (err) {
    // ignore.
    // }
  }

  /**
   * Allows for adding specific types of listeners.
   *
   * @param {*} type
   * @param {*} callback
   * @param {*} param
   */
  addListener = (type, callback, param) => {
    if (type === GRID_CALLBACK_TYPES.MASK_UPDATE) {
      this.masks.addCallback(param, points => {
        callback(new Position(points));
      });
      return;
    }

    this.callbacks = [...this.callbacks, { type, callback, param }];
  };

  /**
   * Allows for removing specific types of listeners
   *
   * @param {string} type
   * @param {string | number} param
   */
  removeListener = (type, param) => {
    if (type === GRID_CALLBACK_TYPES.MASK_UPDATE) {
      this.masks.removeCallback(param);
      return;
    }

    this.callbacks = this.callbacks.filter(
      callback => callback.param !== param,
    );
  };

  /**
   * Starts the sequence based;
   *
   * @param {Array<Object>} sequence
   */
  async startSequence(sequence) {
    for (let i = 0; i < sequence.length; i++) {
      const frame = sequence[i];
      const p = this.grid.draw(frame, 0);

      if (this.masks) {
        await this.masks.draw(frame);
      }

      await p;

      this.fireFrameCompleteCallback(frame.name, i);
    }

    this.fireCompleteCallbacks();
  }

  fireFrameCompleteCallback(name, index) {
    this.callbacks.forEach(({ type, param = null, callback }) => {
      if (type !== GRID_CALLBACK_TYPES.FRAME_COMPLETE) {
        return;
      }

      if (param !== null && param !== name) {
        return;
      }

      callback(index, name);
    });
  }

  /**
   * @TODO:
   */
  fireCompleteCallbacks = () => {
    this.callbacks.forEach(({ type, callback }) => {
      if (type !== GRID_CALLBACK_TYPES.SEQUENCE_COMPLETED) {
        return;
      }
      callback();
    });
  };
}

/**
 * Contains the position data including utility functions.
 */
export class Position {
  constructor(polygon) {
    this.points = polygon.points;
  }

  /**
   * Converts the Position to a bounding rect.
   *
   * @returns {{width: Number, height: Number, top: Number, left: Number}}
   */
  toBoundingRect() {
    const xs = [this.points[0], this.points[2], this.points[4], this.points[6]];
    const ys = [this.points[1], this.points[3], this.points[5], this.points[7]];
    const top = Math.min(...ys);
    const left = Math.min(...xs);

    return {
      top,
      left,
      height: Math.abs(Math.max(...ys) - top),
      width: Math.abs(Math.max(...xs) - left),
    };
  }
}

export default GridPlayer;
